import cloneDeep from 'lodash/cloneDeep'

import { ReservationBlock, ReservationResourceBlock, ReservationCustomerResourceBlock, BreakBlock, BufferTimes } from './block'
import { Section, SectionType } from './section'

import { BookingDates } from '@/_api/models/booking'
import { Employee } from '@/_api/models/employee'
import { ReservationBufferTimeMode } from '@/_api/models/reservationServiceModel'

export class Provider {
  constructor (data, options = {}) {
    this.blocks = []
    this.employees = []
    this.dates = []

    this.options = options

    this.setBlocks(data)
    this.setEmployees(data)
    this.setDates(data)

    this.data = cloneDeep(data.toJSON())
  }

  sections (params) {
    const options = this.options

    let sections = {}

    if (options.type === SectionType.employee) {
      sections = this.buildEmployeeSections(params)
    }

    if (options.type === SectionType.date) {
      sections = this.buildDateSections(params)
    }

    if (options.type === SectionType.employee || options.type === SectionType.date) {
      for (const section of Object.keys(sections)) {
        sections[section]?.calculateCompression()
      }

      this.calculateBufferTimes(sections)
    }

    return sections
  }

  buildEmployeeSections (params = {}) {
    const sections = {}

    const employees = this.employees
    const dates = this.dates
    const type = SectionType.employee

    for (const employee of employees) {
      for (const date of dates) {
        const section = new Section({ employee, date, type })
        const key = section.key

        if (!sections[key]) {
          sections[key] = section
        }
      }
    }

    for (const block of this.blocks) {
      const section = block.section

      if (sections[section]) {
        sections[section].blocks.push(block)
      }
    }

    const sorted = Object
      .entries(sections)
      .filter(([, section]) => {
        if (this.options?.employees?.length) {
          const ids = this.options.employees.map(employee => employee.id)
          return ids.includes(section.employee.idEmployee)
        }

        if (section.employee?.isEmployeeType) {
          return true
        }

        if (params.showResources === 'all') {
          return true
        }

        if (params.showResources === 'active' && section.blocks.length > 0) {
          return true
        }

        return false
      })
      .sort((a, b) => Employee.compare(a[1].employee, b[1].employee))
      .reduce((_sortedObj, [key, value]) => ({
        ..._sortedObj,
        [key]: value,
      }), {})

    return sorted
  }

  buildDateSections (params) {
    const sections = {}

    const options = this.options
    const dates = this.dates
    const type = SectionType.date

    for (const date of dates) {
      const section = new Section({ date, type })
      const key = section.key

      if (!sections[key]) {
        sections[key] = section
      }
    }

    for (const block of this.blocks) {
      const section = block.section

      if (sections[section]) {
        sections[section].blocks.push(block)
      }
    }

    return sections
  }

  reset () {
    const data = new BookingDates(this.data)

    this.blocks = []
    this.employees = []
    this.dates = []

    this.setBlocks(data)
    this.setEmployees(data)
    this.setDates(data)
  }

  range (minuteSize = 2) {
    const starts = []
    const finishes = []

    for (const block of this.blocks) {
      starts.push(block.startTime)
      finishes.push(block.finishTime)
    }

    for (const date of this.dates) {
      if (date.rosters) {
        for (const roster of date.rosters) {
          if (roster?.hour?.working) {
            starts.push(roster?.hour?.startTime)
            finishes.push(roster?.hour?.finishTime)
          }
        }
      }
    }

    return {
      startTime: Math.min(...starts) * minuteSize,
      finishTime: Math.max(...finishes) * minuteSize,
    }
  }

  startTime (minuteSize = 2) {
    const blocks = this.blocks
    const starts = blocks.map(block => block.startTime)

    const first = Math.min(...starts)

    return first * minuteSize
  }

  finishTime (minuteSize = 2) {
    const blocks = this.blocks
    const starts = blocks.map(block => block.finishTime)

    const last = Math.max(...starts)

    return last * minuteSize
  }

  /**
   * Iterate through booking info dates and create blocks
   * @param {BookingInfo} data
   */

  setBlocks (data) {
    const dates = data.dates

    for (const date of dates) {
      if (date.rosters) {
        this.addBreaks(date)
      }
      this.addReservations(date)
    }
  }

  /**
   * Iterate reservations for a date and create blocks for services and resources
   * @param {BookingDatesInfo} date
   */
  addReservations (date) {
    for (const reservation of date.reservations) {
      if (reservation.original) {
        delete reservation.original
      }

      reservation.original = cloneDeep(reservation)

      const servicesBlocks = this.addServices(reservation)
      const resourcesBlocks = this.addResources(reservation)

      const relatedBlocks = [].concat(servicesBlocks, resourcesBlocks)

      for (const block of relatedBlocks) {
        block.relatedBlocks = relatedBlocks.filter(otherBlock => otherBlock !== block)
      }
    }
  }

  /**
   * Iternate rosters for a date and create blocks for breaks
   * @param {BookingDatesInfo} date
   */
  addBreaks (date) {
    const result = []
    const rosters = date.rosters.filter(roster => !!roster.employee)

    for (const roster of rosters) {
      for (const employeeBreak of roster.breaks) {
        employeeBreak.employee = cloneDeep(roster.employee)
        employeeBreak.date = cloneDeep(date.date)

        employeeBreak.original = cloneDeep(employeeBreak)

        const type = this.options.type
        const block = new BreakBlock(employeeBreak, { type })

        result.push(block)
      }
    }

    this.blocks.push(...result)
  }

  /**
   * Iternate services in reservation and create blocks for each
   * @param {Reservation} reservation
   * @returns blocks
   */
  addServices (reservation) {
    const result = []
    const services = reservation.services.filter(reservationService => !!reservationService.employee)

    for (const reservationService of services) {
      const blocks = this.createReservationBlocks(reservationService, reservation)

      result.push(...blocks)
    }

    this.blocks.push(...result)

    return result
  }

  /**
   * Iterate resources in reservation and create blocks for each
   * @param {Reservation} reservation
   * @returns blocks
   */
  addResources (reservation) {
    const result = []

    for (const reservationService of reservation.services) {
      const resources = (reservationService.resources || []).filter(reservationResource => !!reservationResource.resource)

      for (const reservationResource of resources) {
        const blocks = this.createReservationResourceBlocks(reservationService, reservation, reservationResource, reservationResource.resource)

        result.push(...blocks)
      }
    }

    if (reservation.attendees) {
      const resources = (reservation.attendees.resources || []).filter(reservationCustomerResource => !!reservationCustomerResource.resource)

      for (const reservationCustomerResource of resources) {
        const blocks = this.createReservationCustomerResourceBlocks(reservation.services[0], reservation, reservationCustomerResource)

        result.push(...blocks)
      }
    }

    this.blocks.push(...result)

    return result
  }

  /**
   * Create reservation blocks for a service
   * @param {ReservationService} reservationService
   * @param {Reservation} reservation
   * @returns blocks
   */
  createReservationBlocks (reservationService, reservation) {
    const type = this.options.type
    const block = new ReservationBlock(reservation, reservationService, { type })
    const returns = [block]

    const finishingBlock = block.reservationFinishingBlock()
    if (finishingBlock) {
      returns.push(finishingBlock)
    }

    return returns
  }

  /**
   * Create resource blocks for a service resource
   * @param {ReservationService} reservationService
   * @param {Reservation} reservation
   * @param {Resource} resource
   * @returns blocks
   */
  createReservationResourceBlocks (reservationService, reservation, resource) {
    const block = new ReservationResourceBlock(reservation, reservationService, resource)
    const returns = [block]

    const finishingBlock = block.reservationFinishingBlock()

    if (finishingBlock) {
      returns.push(finishingBlock)
    }

    return returns
  }

  /**
   * Create resource blocks for a customer resource
   * @param {ReservationService} reservationService
   * @param {Reservation} reservation
   * @param {Resource} resource
   * @returns blocks
   */
  createReservationCustomerResourceBlocks (reservationService, reservation, resource) {
    const block = new ReservationCustomerResourceBlock(reservation, reservationService, resource)
    const returns = [block]

    const finishingBlock = block.reservationFinishingBlock()
    if (finishingBlock) {
      returns.push(finishingBlock)
    }

    return returns
  }

  calculateBufferTimes (sections) {
    const blocks = Object.values(sections)
      .flatMap(({ blocks }) => blocks)
      .filter(block => (block instanceof ReservationBlock) && !(block instanceof ReservationResourceBlock))
      .reduce((acc, cur) => {
        const id = cur.reservation.idReservation
        acc[id] = [...acc[id] || [], cur]
        return acc
      }, {})

    for (const reservation of Object.values(blocks)) {
      const sortedReservationBlocks = reservation
        .sort((a, b) => (a.startTime > b.startTime) ? 1 : ((b.startTime > a.startTime) ? -1 : 0))
      this.calculateBufferTimesForBlocks(sortedReservationBlocks)
    }
  }

  calculateBufferTimesForBlocks (blocks) {
    for (const block of blocks) {
      block.applyInterserviceBufferTimes()
    }

    const reservationAggregateBlocks = blocks
      .filter(({ durationBlock, reservationService }) => {
        return !durationBlock && reservationService.bufferTimeMode === ReservationBufferTimeMode.reservationAggregate
      })

    const aggregatePreBuffer = reservationAggregateBlocks
      .reduce((acc, { reservationService }) => acc + reservationService.bufferTimePre, 0)

    const aggregatePostBuffer = reservationAggregateBlocks
      .reduce((acc, { reservationService }) => acc + reservationService.bufferTimePost, 0)

    const longestBlocks = blocks
      .filter(({ durationBlock, reservationService }) => {
        return !durationBlock && reservationService.bufferTimeMode === ReservationBufferTimeMode.reservationLongest
      })

    const longestPreBuffer = longestBlocks
      .reduce((max, p) => {
        return p.reservationService.bufferTimePre > max ? p.reservationService.bufferTimePre : max
      }, 0)

    const longestPostBuffer = longestBlocks
      .reduce((max, p) => {
        return p.reservationService.bufferTimePost > max ? p.reservationService.bufferTimePost : max
      }, 0)

    const firstBlock = blocks[0]
    firstBlock.bufferTimes = new BufferTimes(
      (firstBlock.bufferTimes?.preBuffer || 0) + aggregatePreBuffer + longestPreBuffer,
      firstBlock.bufferTimes?.postBuffer || 0
    )

    const lastBlock = blocks.sort((a, b) => b.finishTime - a.finishTime)[0]
    lastBlock.bufferTimes = new BufferTimes(
      lastBlock.bufferTimes?.preBuffer || 0,
      (lastBlock.bufferTimes?.postBuffer || 0) + aggregatePostBuffer + longestPostBuffer
    )
  }

  setEmployees (data) {
    this.employees = data.employees
  }

  setDates (data) {
    this.dates = data.dates
  }
}
